import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API, formatErr, useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldStar, Vault, Upload, Trash, Plus, Check, Users, Bank,
  CreditCard, ArrowRight, Warning, Sparkle, PencilSimple, X, CaretDown,
  CaretUp, FilePdf,
} from "@phosphor-icons/react";

// ============================================================================
// Finansal Kontrol Merkezi — Hard-coded whitelist ile korunan gizli modül
// ============================================================================
export default function FinancialControl() {
  const { financialAccess, loading: authLoading, user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("upload"); // upload | documents | staging | approved | debt | whitelist

  // Yetki kontrolü: yetki yoksa 404 gibi davran (Navigate to /)
  useEffect(() => {
    if (!authLoading && !financialAccess.has_access) {
      // Modül görünmez olmalı; ana sayfaya yönlendir
      nav("/", { replace: true });
    }
  }, [authLoading, financialAccess.has_access, nav]);

  if (authLoading) return null;
  if (!financialAccess.has_access) return null;

  return (
    <Shell>
      <div className="space-y-4" data-testid="financial-control-page">
        {/* Başlık */}
        <div className="flex items-center justify-between gap-4 border-b-2 border-[#1B4776] pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#1B4776] flex items-center justify-center rounded-none">
              <Vault size={26} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-slate-500">
                Gizli Modül
              </div>
              <div className="text-xl font-bold tracking-tight font-heading text-slate-900">
                Finansal Kontrol Merkezi
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-none px-2.5 py-1">
              <ShieldStar size={12} className="mr-1.5" />
              {financialAccess.is_root ? "Yetkili Sahibi" : "Yetkili Kullanıcı"}
            </Badge>
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              {user?.email}
            </span>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          <TabButton active={tab === "upload"} onClick={() => setTab("upload")}
                      icon={<Upload size={14} />} label="1. Yükleme Ekranı" testid="tab-upload" />
          <TabButton active={tab === "documents"} onClick={() => setTab("documents")}
                      icon={<Bank size={14} />} label="2. Yüklenen Belgeler" testid="tab-documents" />
          <TabButton active={tab === "staging"} onClick={() => setTab("staging")}
                      icon={<PencilSimple size={14} />} label="3. İnceleme & Onay" testid="tab-staging" />
          <TabButton active={tab === "approved"} onClick={() => setTab("approved")}
                      icon={<Check size={14} />} label="4. Hareket İşlemleri" testid="tab-entries" />
          <TabButton active={tab === "summary"} onClick={() => setTab("summary")}
                      icon={<Bank size={14} />} label="5. Ekstre Özeti" testid="tab-summary" />
          <TabButton active={tab === "debt"} onClick={() => setTab("debt")}
                      icon={<Users size={14} />} label="Ortak Borç/Alacak" testid="tab-debt" />
          {financialAccess.is_root && (
            <TabButton active={tab === "whitelist"} onClick={() => setTab("whitelist")}
                        icon={<ShieldStar size={14} />} label="Yetkili E-postalar" testid="tab-whitelist" />
          )}
        </div>

        {/* Tab içerikleri */}
        {tab === "upload" && <UploadTab />}
        {tab === "documents" && <DocumentsTab />}
        {tab === "staging" && <EntriesTab mode="staging" />}
        {tab === "approved" && <EntriesTab mode="approved" />}
        {tab === "summary" && <SummaryTab />}
        {tab === "debt" && <DebtTab />}
        {tab === "whitelist" && financialAccess.is_root && <WhitelistTab />}
      </div>
    </Shell>
  );
}

function TabButton({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors ${
        active
          ? "text-[#1B4776] border-[#1B4776]"
          : "text-slate-500 border-transparent hover:text-slate-800"
      }`}
    >
      {icon}{label}
    </button>
  );
}

// ============================================================================
// TAB 1 — Yükleme Ekranı: sadece dosya yükleme + parsing/error kayıtlar
// ============================================================================
function UploadTab() {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/financial/statements`);
      // Yalnızca parsing veya hatalı olanlar bu ekranda
      setRows((data || []).filter((r) => r.parse_status !== "done"));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const anyParsing = rows.some((r) => r.parse_status === "parsing");
    if (!anyParsing) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/financial/statements`);
        (data || []).forEach((nr) => {
          const prev = rows.find((r) => r.id === nr.id);
          if (prev && prev.parse_status === "parsing" && nr.parse_status === "done") {
            toast.success(`${nr.filename}: ${nr.entry_count} işlem ayrıştırıldı → "Yüklenen Belgeler" sekmesinde görünür`);
          }
          if (prev && prev.parse_status === "parsing" && nr.parse_status === "error") {
            toast.error(`${nr.filename}: ${nr.parse_error || "Ayrıştırılamadı"}`);
          }
        });
        setRows((data || []).filter((r) => r.parse_status !== "done"));
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(t);
  }, [rows]);

  const upload = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/financial/statements/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.info(data.message || "Ekstre arka planda ayrıştırılıyor...");
      setFile(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  };

  const del = async (sid) => {
    if (!window.confirm("Bu ekstreyi silmek istiyor musunuz?")) return;
    try {
      await axios.delete(`${API}/financial/statements/${sid}`);
      toast.success("Ekstre silindi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const statusBadge = (r) => {
    if (r.parse_status === "parsing") {
      return (
        <Badge className="bg-amber-50 text-amber-800 border border-amber-300 rounded-none text-[9px]">
          <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse mr-1" />
          AYRIŞTIRILIYOR
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-50 text-red-800 border border-red-300 rounded-none text-[9px]" title={r.parse_error || ""}>
        HATA
      </Badge>
    );
  };

  return (
    <>
      <Card className="rounded-none border-slate-200 p-5 bg-gradient-to-br from-[#F7FBE9] to-white">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#1B4776] mb-3 flex items-center gap-2">
          <Upload size={16} weight="duotone" /> Banka veya Kredi Kartı Ekstresi Yükle
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[280px]">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1 block">
              PDF veya Excel Dosyası
            </Label>
            <Input
              type="file"
              accept=".pdf,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="financial-upload-input"
              className="rounded-none border-slate-300 file:mr-3 file:py-1 file:px-3 file:text-xs file:font-bold file:bg-[#1B4776] file:text-white file:border-0 file:cursor-pointer"
            />
            <div className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-1.5">
              <Sparkle size={11} className="text-[#95C11F]" />
              AI arka planda çalışır — yükleme saniyeler içinde biter. Başarılı ekstreler "Yüklenen Belgeler" sekmesine düşer.
            </div>
          </div>
          <Button
            onClick={upload}
            disabled={!file || uploading}
            data-testid="financial-upload-btn"
            className="rounded-none bg-[#1B4776] hover:bg-[#153859] text-white uppercase tracking-widest text-[11px] font-bold px-5"
          >
            <Upload size={14} className="mr-1.5" />
            {uploading ? "Yükleniyor..." : "Yükle & AI Ayrıştır"}
          </Button>
        </div>
      </Card>

      <Card className="rounded-none border-slate-200 mt-4">
        <div className="p-4 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-widest font-bold text-slate-700">
          Aktif Yüklemeler ({rows.length})
          <span className="text-slate-400 ml-2 normal-case tracking-normal">— Sadece ayrıştırılan / hatalı kayıtlar burada görünür</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3 font-bold">Dosya</th>
                <th className="text-left p-3 font-bold">Durum</th>
                <th className="text-left p-3 font-bold">Yükleyen / Tarih</th>
                <th className="text-left p-3 font-bold">Detay</th>
                <th className="text-right p-3 font-bold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-slate-400 text-xs">
                  Aktif yükleme yok. Bir dosya yüklediğinizde burada işlem durumu görünecek.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`upload-row-${r.id}`}>
                  <td className="p-3">
                    <div className="text-slate-800 font-medium">{r.filename}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{r.source_type}</div>
                  </td>
                  <td className="p-3">{statusBadge(r)}</td>
                  <td className="p-3 text-[10px] text-slate-600">
                    <div>{r.uploaded_by_email}</div>
                    <div className="text-slate-400">{new Date(r.uploaded_at).toLocaleString("tr-TR")}</div>
                  </td>
                  <td className="p-3 text-[11px] text-red-700">
                    {r.parse_error || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => del(r.id)}
                            data-testid={`upload-del-${r.id}`}
                            className="rounded-none h-7 border-red-300 text-red-700 hover:bg-red-50">
                      <Trash size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}


// ============================================================================
// TAB 2 — Yüklenen Belgeler: başarılı ayrıştırılmış ekstreler + düzenlenebilir metadata
// ============================================================================
function DocumentsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sortBy, setSortBy] = useState("uploaded_at");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [accountFilter, setAccountFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all"); // YYYY-MM | "all"

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/financial/statements`);
      setRows((data || []).filter((r) => r.parse_status === "done"));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (sid) => {
    if (!window.confirm("Bu belgeyi ve bütçeye aktarılmamış tüm satırlarını silmek istiyor musunuz?")) return;
    try {
      await axios.delete(`${API}/financial/statements/${sid}`);
      toast.success("Belge silindi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  // Dönem seçenekleri: period_from ve period_to'dan YYYY-MM çıkar
  const periodOptions = useMemo(() => {
    const months = new Set();
    for (const r of rows) {
      for (const d of [r.period_from, r.period_to]) {
        if (typeof d === "string" && d.length >= 7 && d[4] === "-") {
          months.add(d.slice(0, 7));
        }
      }
    }
    return Array.from(months).sort().reverse();
  }, [rows]);

  const monthLabel = (ym) => {
    if (!ym || ym.length < 7) return ym;
    const [y, m] = ym.split("-");
    const names = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
    return `${names[Number(m) - 1] || m} ${y}`;
  };

  const filteredRows = useMemo(() => {
    const q = accountFilter.trim().toLowerCase();
    return rows.filter((r) => {
      // Hesap bilgisi filtresi — IBAN, hesap no, kart maskesi ve banka adı üzerinde arama
      if (q) {
        const hay = [
          r.iban, r.account_number, r.card_mask, r.bank_name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Dönem filtresi — period_from veya period_to'nun YYYY-MM prefix'i eşleşmeli
      if (periodFilter !== "all") {
        const pf = (r.period_from || "").slice(0, 7);
        const pt = (r.period_to || "").slice(0, 7);
        if (pf !== periodFilter && pt !== periodFilter) return false;
      }
      return true;
    });
  }, [rows, accountFilter, periodFilter]);

  const sortedRows = useMemo(() => {
    const getVal = (r) => {
      switch (sortBy) {
        case "statement_code": return (r.statement_code || "").toLowerCase();
        case "filename": return (r.filename || "").toLowerCase();
        case "bank_name": return (r.bank_name || "").toLowerCase();
        case "account": return (r.iban || r.account_number || r.card_mask || "").toLowerCase();
        case "period_from": return r.period_from || "";
        case "status": return Number(r.deleted_entry_count || 0);
        case "entry_count": return Number(r.entry_count || 0);
        case "uploaded_at":
        default: return r.uploaded_at || r.created_at || "";
      }
    };
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va === vb) return 0;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb), "tr")
        : String(vb).localeCompare(String(va), "tr");
    });
    return arr;
  }, [filteredRows, sortBy, sortDir]);

  const clearFilters = () => {
    setAccountFilter("");
    setPeriodFilter("all");
  };
  const activeFilterCount = (accountFilter.trim() ? 1 : 0) + (periodFilter !== "all" ? 1 : 0);

  const SortHeader = ({ label, k, align = "left" }) => {
    const active = sortBy === k;
    const alignCls = align === "right" ? "text-right" : "text-left";
    return (
      <th
        className={`${alignCls} p-3 font-bold cursor-pointer select-none hover:bg-slate-200 transition-colors`}
        onClick={() => toggleSort(k)}
        data-testid={`sort-header-${k}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active ? (
            sortDir === "asc"
              ? <CaretUp size={10} weight="bold" className="text-[#1B4776]" />
              : <CaretDown size={10} weight="bold" className="text-[#1B4776]" />
          ) : (
            <CaretDown size={10} className="text-slate-300" />
          )}
        </span>
      </th>
    );
  };

  return (
    <>
      <Card className="rounded-none border-slate-200">
        <div className="p-4 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-widest font-bold text-slate-700 flex items-center justify-between gap-3 flex-wrap">
          <span>
            Yüklenen Belgeler ({sortedRows.length}
            {sortedRows.length !== rows.length && <span className="text-slate-500 normal-case font-normal"> / {rows.length}</span>})
          </span>
          <span className="text-[10px] text-slate-500 normal-case tracking-normal font-normal">
            Sütun başlığına tıklayarak sıralayın · Satır sağındaki ✏ ile düzenleyin
          </span>
        </div>
        {/* Filtre çubuğu */}
        <div className="p-3 border-b border-slate-200 bg-white flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
              Hesap Bilgisi
            </Label>
            <Input
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              placeholder="IBAN, hesap no, kart maskesi, banka..."
              className="rounded-none h-8 text-xs w-[280px]"
              data-testid="doc-filter-account"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
              Dönem
            </Label>
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="rounded-none h-8 text-xs w-[180px]" data-testid="doc-filter-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Dönemler</SelectItem>
                {periodOptions.map((p) => (
                  <SelectItem key={p} value={p}>{monthLabel(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="rounded-none h-8 text-[10px] uppercase tracking-widest font-bold border-slate-300"
              data-testid="doc-filter-clear"
            >
              <X size={11} className="mr-1" /> Filtreleri Temizle ({activeFilterCount})
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
              <tr>
                <SortHeader label="Kod" k="statement_code" />
                <SortHeader label="Dosya" k="filename" />
                <SortHeader label="Banka / Kart" k="bank_name" />
                <SortHeader label="Hesap / IBAN / Kart Bilgisi" k="account" />
                <SortHeader label="Dönem" k="period_from" />
                <SortHeader label="Durum" k="status" />
                <SortHeader label="Satır" k="entry_count" align="right" />
                <th className="text-right p-3 font-bold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-xs">
                  {rows.length === 0
                    ? 'Henüz belge yok. "Yükleme Ekranı" sekmesinden ekstre yükleyin.'
                    : "Filtre kriterlerine uyan belge bulunamadı."}
                </td></tr>
              )}
              {sortedRows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`document-row-${r.id}`}>
                  <td className="p-3">
                    <span className="inline-block px-2 py-1 bg-[#1B4776] text-white font-mono text-[11px] font-bold tracking-wider"
                          data-testid={`document-code-${r.id}`}>
                      {r.statement_code || "—"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="text-slate-800 font-medium">{r.filename}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{r.source_type}</div>
                  </td>
                  <td className="p-3 text-[11px]">
                    {r.bank_name ? (
                      <div className="font-bold text-[#1B4776]">{r.bank_name}</div>
                    ) : <span className="text-slate-400">—</span>}
                    {r.card_mask && (
                      <div className="mt-0.5">
                        <Badge className="bg-purple-50 text-purple-800 border border-purple-300 rounded-none text-[9px]">
                          <CreditCard size={9} className="mr-1" />
                          KART
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-[11px]">
                    {r.iban && (
                      <div className="font-mono text-slate-700 text-[11px]" title={r.iban}>
                        <span className="text-[9px] uppercase text-slate-400 mr-1">IBAN</span>
                        {r.iban}
                      </div>
                    )}
                    {r.account_number && (
                      <div className="text-slate-600 mt-0.5">
                        <span className="text-[9px] uppercase tracking-widest text-slate-400 mr-1">Hesap</span>
                        <span className="font-mono">{r.account_number}</span>
                      </div>
                    )}
                    {r.card_mask && (
                      <div className="text-slate-700 font-mono mt-0.5">
                        <span className="text-[9px] uppercase tracking-widest text-slate-400 mr-1">Kart</span>
                        {r.card_mask}
                      </div>
                    )}
                    {!r.iban && !r.account_number && !r.card_mask && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3 text-[11px] text-slate-700">
                    {r.period_from && r.period_to
                      ? `${r.period_from} → ${r.period_to}`
                      : (r.period_label || <span className="text-slate-400">—</span>)}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-none text-[9px] self-start">
                        HAZIR
                      </Badge>
                      {r.deleted_entry_count > 0 && (
                        <Badge
                          className="bg-red-50 text-red-800 border border-red-300 rounded-none text-[9px] self-start"
                          title={r.last_deletion_at ? `Son silme: ${new Date(r.last_deletion_at).toLocaleString("tr-TR")}` : ""}
                          data-testid={`document-deleted-badge-${r.id}`}
                        >
                          <Warning size={9} className="mr-1" weight="fill" />
                          {r.deleted_entry_count} SİLİNEN KAYIT VARDIR
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right font-bold text-[#1B4776]">{r.entry_count}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => setEditing(r)}
                            data-testid={`document-edit-${r.id}`}
                            className="rounded-none h-7 border-slate-300 mr-1">
                      <PencilSimple size={12} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => del(r.id)}
                            data-testid={`document-del-${r.id}`}
                            className="rounded-none h-7 border-red-300 text-red-700 hover:bg-red-50">
                      <Trash size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {editing && (
        <StatementEditDialog
          statement={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}


function StatementEditDialog({ statement, onClose, onSaved }) {
  // Legacy formatı (DD/MM/YYYY veya DD.MM.YYYY) → ISO (YYYY-MM-DD) parse et
  const parseAnyDate = (v) => {
    if (!v || typeof v !== "string") return "";
    // Zaten ISO ise
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    // TR (DD/MM/YYYY veya DD.MM.YYYY)
    const m = v.match(/^(\d{2})[./](\d{2})[./](\d{4})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return "";
  };
  const [form, setForm] = useState({
    bank_name: statement.bank_name || "",
    iban: statement.iban || "",
    account_number: statement.account_number || "",
    card_mask: statement.card_mask || "",
    period_from: parseAnyDate(statement.period_from),
    period_to: parseAnyDate(statement.period_to),
    period_label: statement.period_label || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/financial/statements/${statement.id}`, form);
      toast.success(`Kaydedildi. Yeni kod: ${data.statement_code}`);
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-xl" data-testid="statement-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Belge Bilgilerini Düzenle</DialogTitle>
          <DialogDescription className="text-xs">
            Değişiklikler sonrası ekstre kodu otomatik yeniden üretilir (EKS/KRT-son4-YYMM).
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Banka Adı</Label>
            <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    className="rounded-none h-9 text-sm" data-testid="stmt-edit-bank" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">IBAN</Label>
            <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
                    placeholder="TR..."
                    className="rounded-none h-9 text-sm font-mono" data-testid="stmt-edit-iban" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Hesap Numarası</Label>
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    className="rounded-none h-9 text-sm font-mono" data-testid="stmt-edit-account" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Kart Bilgisi (maskeli)</Label>
            <Input value={form.card_mask} onChange={(e) => setForm({ ...form, card_mask: e.target.value })}
                    placeholder="1234 **** **** 5678"
                    className="rounded-none h-9 text-sm font-mono" data-testid="stmt-edit-card" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Dönem Başlangıç</Label>
            <Input
              type="date"
              value={form.period_from}
              onChange={(e) => setForm({ ...form, period_from: e.target.value })}
              className="rounded-none h-9 text-sm"
              data-testid="stmt-edit-period-from"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Dönem Bitiş</Label>
            <Input
              type="date"
              value={form.period_to}
              onChange={(e) => setForm({ ...form, period_to: e.target.value })}
              className="rounded-none h-9 text-sm"
              data-testid="stmt-edit-period-to"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
              Dönem Etiketi (alternatif — "Ağustos 2025" gibi)
            </Label>
            <Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })}
                    placeholder="Ağustos 2025"
                    className="rounded-none h-9 text-sm" data-testid="stmt-edit-period-label" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none uppercase tracking-widest text-[10px]">
            İptal
          </Button>
          <Button onClick={save} disabled={saving} data-testid="stmt-edit-save"
                  className="rounded-none bg-[#1B4776] hover:bg-[#153859] uppercase tracking-widest text-[10px]">
            {saving ? "Kaydediliyor..." : "Kaydet ve Kodu Yenile"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TAB 2 — Entries: İşlemler CRUD + Push to Budget
// ============================================================================
function EntriesTab({ mode = "staging" }) {
  const isStaging = mode === "staging";
  const [entries, setEntries] = useState([]);
  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statements, setStatements] = useState([]);   // Ekstre filtresi için (Faz 34.27)
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState({ direction: "", nature: "", link: "", category: "", supplier: "", statement: "", period: "", q: "" });
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [editing, setEditing] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(
        `${API}/financial/entries?approved=${isStaging ? "false" : "true"}`
      );
      const list = data || [];
      setEntries(list);
      // Güvenlik: yüklenen listede olmayan seçili ID'leri temizle
      // (örn. bulk-update sonrası satırlar başka tab'a geçti — eski seçim taşınmasın)
      setSelected((prev) => {
        if (prev.size === 0) return prev;
        const ids = new Set(list.map((e) => e.id));
        const next = new Set();
        prev.forEach((id) => { if (ids.has(id)) next.add(id); });
        return next.size === prev.size ? prev : next;
      });
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    setSelected(new Set());
    axios.get(`${API}/sites`).then((r) => {
      const list = r.data || [];
      // Şantiye adına göre Türkçe alfabetik sıralama — Toplu Düzenleme "Proje seçin"
      // dropdown'ında ve tekil düzenleme dialogunda tutarlı sıralama sağlar.
      list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
      setSites(list);
    }).catch(() => {});
    axios.get(`${API}/suppliers`).then((r) => setSuppliers(r.data || [])).catch(() => {});
    // Ekstre filtresi için: yüklenen (parse_status=done) belgeleri çek (Faz 34.27)
    axios.get(`${API}/financial/statements`).then((r) => {
      const arr = (r.data || []).filter((s) => s.parse_status === "done");
      arr.sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
      setStatements(arr);
    }).catch(() => {});
    // Kategorileri hem sistem ayarlarından hem de kullanıcının daha önce yazdığı
    // özel kategorilerden çekip birleştir (hafıza — önceki kategoriler öneri olarak gelir).
    Promise.all([
      axios.get(`${API}/settings/budget-categories`).then((r) => (r.data || [])
        .map((c) => c.name || c.key || c)).catch(() => (
          ["Malzeme", "İşçilik", "Nakliye", "Yakıt", "Kira", "Fatura", "Vergi", "Ödeme", "Diğer"]
        )),
      axios.get(`${API}/financial/categories`).then((r) => (r.data?.items || [])).catch(() => []),
    ]).then(([builtin, learned]) => {
      // Benzersiz + alfabetik sıralı liste (case-insensitive)
      const seen = new Map();
      [...builtin, ...learned].forEach((c) => {
        const s = (c || "").trim();
        if (!s) return;
        const key = s.toLowerCase();
        if (!seen.has(key)) seen.set(key, s);
      });
      setCategories(Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "tr")));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Kategorize edilmiş sayılır:
  //  - Hesap transferi (virman) → her zaman kategorize (kategori otomatik atanır)
  //  - Kişisel harcama → kategori otomatik "Kişisel" atanır, bağlantı gerekmez
  //  - Diğerleri → (proje veya merkez bağlantısı) + kategori dolu
  const isCategorized = (e) => {
    if (e.direction === "hesap_transferi") return true;
    if (e.nature === "personal" && !!e.linked_category) return true;
    return !!(e.linked_category) && ((e.link_type === "merkez") || !!e.linked_site_id);
  };

  const filtered = useMemo(() => {
    const arr = entries.filter((e) => {
      if (filter.direction && e.direction !== filter.direction) return false;
      if (filter.nature && e.nature !== filter.nature) return false;
      if (filter.link === "merkez" && e.link_type !== "merkez") return false;
      if (filter.link === "project" && e.link_type !== "project") return false;
      if (filter.link === "unlinked" && (e.link_type || e.linked_category)) return false;
      if (filter.category && (e.linked_category || "") !== filter.category) return false;
      if (filter.supplier && (e.linked_supplier_name || "") !== filter.supplier) return false;
      if (filter.statement && e.statement_id !== filter.statement) return false;
      if (filter.period) {
        // "YYYY-MM" veya "YYYY" prefix eşleşmesi (tarihler YYYY-MM-DD formatında)
        const d = String(e.date || "");
        if (!d.startsWith(filter.period)) return false;
      }
      if (filter.q) {
        const q = filter.q.toLowerCase();
        return (e.description || "").toLowerCase().includes(q) ||
               (e.partner_email || "").toLowerCase().includes(q);
      }
      return true;
    });
    // Sıralama
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    // Tutar için işaretli değer: gider → negatif, gelir → pozitif,
    // hesap_transferi → orijinal yön (gelir=+, gider=-, bilinmiyor=0)
    const signedAmount = (e) => {
      const amt = Number(e.amount_gross || 0);
      if (e.direction === "gider") return -amt;
      if (e.direction === "gelir") return amt;
      if (e.direction === "hesap_transferi") {
        if (e.original_direction === "gelir") return amt;
        if (e.original_direction === "gider") return -amt;
        return 0;
      }
      return amt;
    };
    return arr.sort((a, b) => {
      let va, vb;
      if (key === "amount_gross") { va = signedAmount(a); vb = signedAmount(b); }
      else if (key === "categorized") { va = isCategorized(a) ? 1 : 0; vb = isCategorized(b) ? 1 : 0; }
      else { va = (a[key] || "").toString().toLowerCase(); vb = (b[key] || "").toString().toLowerCase(); }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [entries, filter, sort]);

  // Filtre için benzersiz kategori ve tedarikçi/ekip/personel listeleri
  const distinctCategories = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => { if (e.linked_category) s.add(e.linked_category); });
    return Array.from(s).sort((a, b) => a.localeCompare(b, "tr"));
  }, [entries]);
  const distinctSuppliers = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => { if (e.linked_supplier_name) s.add(e.linked_supplier_name); });
    return Array.from(s).sort((a, b) => a.localeCompare(b, "tr"));
  }, [entries]);

  // Dönemsel filtre: Yıllar + o yıl içindeki aylar (en yeni üstte)
  const distinctPeriods = useMemo(() => {
    const years = new Map();  // "YYYY" -> Set of "YYYY-MM"
    entries.forEach((e) => {
      const d = String(e.date || "");
      if (d.length < 7) return;
      const ym = d.slice(0, 7);
      const y = d.slice(0, 4);
      if (!years.has(y)) years.set(y, new Set());
      years.get(y).add(ym);
    });
    const ordered = Array.from(years.keys()).sort().reverse();
    return ordered.map((y) => ({
      year: y,
      months: Array.from(years.get(y)).sort().reverse(),
    }));
  }, [entries]);
  const MONTH_LABELS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                         "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  const periodLabel = (v) => {
    if (!v) return "Tüm Dönemler";
    if (v.length === 4) return v; // YYYY
    if (v.length === 7) {
      const [y, m] = v.split("-");
      return `${MONTH_LABELS[Number(m) - 1] || m} ${y}`;
    }
    return v;
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };
  const SortHead = ({ colKey, children, align = "left" }) => (
    <button
      type="button"
      onClick={() => toggleSort(colKey)}
      data-testid={`sort-${colKey}`}
      className={`w-full flex items-center gap-1 ${align === "right" ? "justify-end" : align === "center" ? "justify-center" : ""} hover:text-[#1B4776] transition-colors`}
    >
      <span>{children}</span>
      <span className="text-[8px] text-slate-400">
        {sort.key === colKey ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}
      </span>
    </button>
  );

  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((e) => e.id)));
  };

  // Onaya izin: seçili satırların HEPSİ kategorize edilmiş olmalı
  const selectedRows = filtered.filter((e) => selected.has(e.id));
  const uncatSelected = selectedRows.filter((e) => !isCategorized(e));
  const canApprove = selected.size > 0 && uncatSelected.length === 0;

  const doBulkApprove = async (approve) => {
    if (selected.size === 0) { toast.error("Satır seçin"); return; }
    if (approve && uncatSelected.length > 0) {
      toast.error(`Onay için ${uncatSelected.length} satırın önce kategorize edilmesi gerekir (Bağlantı + Kategori)`);
      return;
    }
    if (!approve) {
      const ok = window.confirm(
        `${selected.size} kaydın onayını geri almak istediğinize emin misiniz?\n\n` +
        `• Kayıtlar "Düzenleme Bekliyor" konumuna dönecek (tüm alanlar tekrar düzenlenebilir olacak).\n` +
        `• Bütçeye aktarılmış olanların proje kayıtları da SİLİNECEK.\n` +
        `• Yön (Gelir/Gider) ham ekstre değerine geri yüklenecek.`
      );
      if (!ok) return;
    }
    try {
      const { data } = await axios.post(`${API}/financial/entries/bulk-approve`, {
        entry_ids: Array.from(selected), approved: approve,
      });
      if (approve) {
        toast.success(`${data.updated} kayıt onaylandı → Hareket İşlemleri'ne taşındı`);
      } else {
        const budgetPart = data.deleted_budget_entries
          ? ` · ${data.deleted_budget_entries} bütçe kaydı silindi`
          : "";
        toast.success(`${data.updated} kayıt geri çekildi — Düzenleme Bekliyor'a döndü${budgetPart}`);
      }
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const pushToBudget = async () => {
    if (selected.size === 0) { toast.error("Satır seçin"); return; }
    setPushing(true);
    try {
      const { data } = await axios.post(`${API}/financial/entries/push-to-budget`, {
        entry_ids: Array.from(selected),
      });
      toast.success(`${data.pushed} kayıt bütçeye aktarıldı, ${data.skipped} atlandı`);
      if (data.errors?.length) toast.warning(data.errors.join(" • "));
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPushing(false); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) { toast.error("Satır seçin"); return; }
    if (!window.confirm(`${selected.size} kaydı silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz. İlgili ekstre "Yüklenen Belgeler" sekmesinde "silinen kayıt vardır" uyarısıyla işaretlenecektir.`)) return;
    try {
      const { data } = await axios.post(`${API}/financial/entries/bulk-delete`, {
        entry_ids: Array.from(selected),
      });
      toast.success(`${data.deleted} kayıt silindi`);
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---- EXPORT: Excel + PDF (filtrelenmiş satırlar için) ----
  const exportExcel = () => {
    if (filtered.length === 0) { toast.error("Aktarılacak kayıt yok"); return; }
    const rows = filtered.map((e) => {
      const amt = Number(e.amount_gross || 0);
      const dirLabel = e.direction === "gelir" ? "Gelir"
        : e.direction === "gider" ? "Gider"
        : e.direction === "hesap_transferi" ? "Transfer" : "-";
      let signedAmt = amt;
      if (e.direction === "gider") signedAmt = -amt;
      else if (e.direction === "hesap_transferi") {
        if (e.original_direction === "gelir") signedAmt = amt;
        else if (e.original_direction === "gider") signedAmt = -amt;
        else signedAmt = 0;
      }
      return {
        "Tarih": e.date || "",
        "Açıklama": e.description || "",
        "Ortak": e.partner_email || "",
        "Yön": dirLabel,
        "Tür": e.nature === "personal" ? "Kişisel" : "Şirket",
        "Bağlantı": e.link_type === "merkez" ? "Merkez"
          : e.link_type === "project" ? (e.linked_site_name || "Proje") : "-",
        "Kategori": e.linked_category || "",
        "Tedarikçi/Ekip/Personel": e.linked_supplier_name || "",
        "Tutar (İşaretli)": signedAmt,
        "Tutar (Mutlak)": amt,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Finansal Hareketler");
    const fname = `finansal-hareketler-${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`${rows.length} kayıt Excel'e aktarıldı`);
  };

  const exportPDF = async () => {
    if (filtered.length === 0) { toast.error("Aktarılacak kayıt yok"); return; }
    try {
      const resp = await axios.post(
        `${API}/financial/entries/export-pdf`,
        { entry_ids: filtered.map((e) => e.id) },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `finansal-hareketler-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success(`${filtered.length} kayıt PDF'e aktarıldı`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const applyAiSuggestion = async (e) => {
    const upd = {};
    if (e.ai_suggested_site_id) { upd.linked_site_id = e.ai_suggested_site_id; upd.link_type = "project"; }
    if (e.ai_suggested_supplier_id) upd.linked_supplier_id = e.ai_suggested_supplier_id;
    if (e.ai_suggested_category) upd.linked_category = mapCategoryHint(e.ai_suggested_category, categories);
    if (Object.keys(upd).length === 0) { toast.info("AI önerisi yok"); return; }
    try {
      await axios.put(`${API}/financial/entries/${e.id}`, upd);
      toast.success("AI önerisi uygulandı");
      await load();
    } catch (er) { toast.error(formatErr(er)); }
  };

  const emptyText = isStaging
    ? "Onay bekleyen kayıt yok. Ekstre yükleyin veya farklı filtre deneyin."
    : "Onaylı hareket yok. Önce inceleme sekmesinden onay verin.";

  // Kategorize sayacı (staging için)
  const catCount = entries.filter(isCategorized).length;

  return (
    <>
      <Card className="rounded-none border-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Ara: açıklama, ortak..."
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            className="rounded-none h-8 text-xs flex-1 min-w-[200px]"
            data-testid="entry-filter-q"
          />
          <Select value={filter.direction || "all"} onValueChange={(v) => setFilter({ ...filter, direction: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[140px]"><SelectValue placeholder="Yön" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Yönler</SelectItem>
              <SelectItem value="gelir">Gelir</SelectItem>
              <SelectItem value="gider">Gider</SelectItem>
              <SelectItem value="hesap_transferi">Hesap Transferi</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.nature || "all"} onValueChange={(v) => setFilter({ ...filter, nature: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[130px]"><SelectValue placeholder="Türü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Tür</SelectItem>
              <SelectItem value="company">Şirket</SelectItem>
              <SelectItem value="personal">Kişisel</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.link || "all"} onValueChange={(v) => setFilter({ ...filter, link: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[140px]"><SelectValue placeholder="Bağlantı" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Bağlantı</SelectItem>
              <SelectItem value="project">Proje</SelectItem>
              <SelectItem value="merkez">Merkez</SelectItem>
              <SelectItem value="unlinked">Bağlanmamış</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.category || "all"} onValueChange={(v) => setFilter({ ...filter, category: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[160px]" data-testid="entry-filter-category">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {distinctCategories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.supplier || "all"} onValueChange={(v) => setFilter({ ...filter, supplier: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[190px]" data-testid="entry-filter-supplier">
              <SelectValue placeholder="Tedarikçi/Ekip/Personel" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">Tüm Tedarikçi/Ekip/Personel</SelectItem>
              {distinctSuppliers.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.statement || "all"} onValueChange={(v) => setFilter({ ...filter, statement: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[220px]" data-testid="entry-filter-statement">
              <SelectValue placeholder="Ekstre" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">Tüm Ekstreler</SelectItem>
              {statements.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="font-mono font-bold text-[#1B4776] mr-2">{s.statement_code || "—"}</span>
                  <span className="text-slate-500 text-[10px]">{s.bank_name || s.filename}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.period || "all"} onValueChange={(v) => setFilter({ ...filter, period: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[170px]" data-testid="entry-filter-period">
              <SelectValue placeholder="Dönem" />
            </SelectTrigger>
            <SelectContent className="max-h-[380px]">
              <SelectItem value="all">Tüm Dönemler</SelectItem>
              {distinctPeriods.map((p) => (
                <React.Fragment key={p.year}>
                  <SelectItem value={p.year}>
                    <span className="font-bold text-[#1B4776]">{p.year}</span>
                    <span className="text-[10px] text-slate-400 ml-1">(tüm yıl)</span>
                  </SelectItem>
                  {p.months.map((ym) => (
                    <SelectItem key={ym} value={ym}>
                      <span className="ml-3 text-slate-600">{periodLabel(ym)}</span>
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>
          {(filter.direction || filter.nature || filter.link || filter.category || filter.supplier || filter.statement || filter.period || filter.q) && (
            <button
              type="button"
              onClick={() => setFilter({ direction: "", nature: "", link: "", category: "", supplier: "", statement: "", period: "", q: "" })}
              data-testid="entry-filter-clear"
              className="rounded-none border-2 border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-400 uppercase tracking-widest text-[9px] font-bold px-2 h-8"
              title="Filtreleri temizle"
            >
              ✕ Temizle
            </button>
          )}
          {/* Export butonları — filtrelenmiş satırları PDF / Excel'e aktar */}
          <button
            type="button"
            onClick={exportExcel}
            disabled={filtered.length === 0}
            data-testid="entry-export-excel"
            title="Filtrelenmiş satırları Excel olarak indir"
            className="rounded-none border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 uppercase tracking-widest text-[9px] font-bold px-2 h-8 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ▼ Excel
          </button>
          <button
            type="button"
            onClick={exportPDF}
            disabled={filtered.length === 0}
            data-testid="entry-export-pdf"
            title="Filtrelenmiş satırları PDF olarak indir"
            className="rounded-none border-2 border-red-500 text-red-700 hover:bg-red-50 uppercase tracking-widest text-[9px] font-bold px-2 h-8 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ▼ PDF
          </button>
          <div className="text-[10px] text-slate-500 ml-auto">
            {isStaging && (
              <span className="mr-2">
                <span className="text-emerald-700 font-bold">{catCount}</span> kategorize / {entries.length} toplam ·
              </span>
            )}
            {filtered.length}/{entries.length} kayıt · {selected.size} seçili
            {isStaging && uncatSelected.length > 0 && (
              <span className="text-amber-700 font-bold ml-1">
                ({uncatSelected.length} eksik)
              </span>
            )}
          </div>
          {isStaging ? (
            <>
              <Button
                onClick={() => setBulkOpen(true)}
                disabled={selected.size === 0}
                data-testid="entry-bulk-edit-btn"
                variant="outline"
                className="rounded-none border-[#1B4776] text-[#1B4776] uppercase tracking-widest text-[10px] font-bold h-8 px-3"
              >
                <PencilSimple size={12} className="mr-1" />
                Toplu Düzenle ({selected.size})
              </Button>
              <Button
                onClick={bulkDelete}
                disabled={selected.size === 0}
                data-testid="entry-bulk-delete-btn"
                variant="outline"
                className="rounded-none border-red-400 text-red-700 hover:bg-red-50 uppercase tracking-widest text-[10px] font-bold h-8 px-3"
              >
                <Trash size={12} className="mr-1" />
                Sil ({selected.size})
              </Button>
              <Button
                onClick={() => doBulkApprove(true)}
                disabled={!canApprove}
                data-testid="entry-bulk-approve-btn"
                title={!canApprove && selected.size > 0 ? `${uncatSelected.length} satır kategorize edilmemiş` : ""}
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white uppercase tracking-widest text-[10px] font-bold h-8 px-3"
              >
                <Check size={12} className="mr-1" />
                Onayla ({selected.size})
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => doBulkApprove(false)}
                disabled={selected.size === 0}
                data-testid="entry-bulk-unapprove-btn"
                variant="outline"
                className="rounded-none border-amber-500 text-amber-700 uppercase tracking-widest text-[10px] font-bold h-8 px-3"
              >
                Onayı Geri Al
              </Button>
              <Button
                onClick={bulkDelete}
                disabled={selected.size === 0}
                data-testid="entry-bulk-delete-btn"
                variant="outline"
                className="rounded-none border-red-400 text-red-700 hover:bg-red-50 uppercase tracking-widest text-[10px] font-bold h-8 px-3"
              >
                <Trash size={12} className="mr-1" />
                Sil ({selected.size})
              </Button>
              <Button
                onClick={pushToBudget}
                disabled={selected.size === 0 || pushing}
                data-testid="entry-push-budget-btn"
                className="rounded-none bg-[#95C11F] hover:bg-[#7fa317] text-white uppercase tracking-widest text-[10px] font-bold h-8 px-3"
              >
                <ArrowRight size={12} className="mr-1" />
                {pushing ? "Aktarılıyor..." : `Bütçeye Aktar (${selected.size})`}
              </Button>
            </>
          )}
        </div>
        {isStaging && (
          <div className="mt-2 flex items-center gap-4 text-[10px] text-slate-600 border-t border-slate-100 pt-2">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-300"></span>
              <span>Kategorize edildi — onaya hazır</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 bg-white border border-slate-300"></span>
              <span>Eksik — bağlantı veya kategori atanmalı</span>
            </div>
          </div>
        )}
      </Card>

      <Card className="rounded-none border-slate-200 mt-3 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-700 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="p-2 w-8">
                  <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length}
                          onChange={selectAll} data-testid="entry-select-all" />
                </th>
                <th className="text-left p-2 font-bold"><SortHead colKey="date">Tarih</SortHead></th>
                <th className="text-left p-2 font-bold"><SortHead colKey="description">Açıklama</SortHead></th>
                <th className="text-right p-2 font-bold"><SortHead colKey="amount_gross" align="right">Tutar</SortHead></th>
                <th className="text-center p-2 font-bold"><SortHead colKey="direction" align="center">Yön</SortHead></th>
                <th className="text-center p-2 font-bold"><SortHead colKey="nature" align="center">Tür</SortHead></th>
                <th className="text-left p-2 font-bold"><SortHead colKey="linked_site_name">Bağlantı</SortHead></th>
                <th className="text-left p-2 font-bold"><SortHead colKey="linked_category">Kategori</SortHead></th>
                <th className="text-left p-2 font-bold"><SortHead colKey="linked_supplier_name">Tedarikçi / Ekip / Personel</SortHead></th>
                <th className="text-center p-2 font-bold">
                  {isStaging
                    ? <SortHead colKey="categorized" align="center">Durum</SortHead>
                    : "Durum"}
                </th>
                {isStaging && <th className="text-right p-2 font-bold">İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={isStaging ? 11 : 10} className="p-8 text-center text-slate-400">{emptyText}</td></tr>
              )}
              {filtered.map((e) => {
                const cat = isCategorized(e);
                const rowCls = selected.has(e.id)
                  ? "bg-blue-100"
                  : cat
                    ? "bg-emerald-50/60 hover:bg-emerald-50"
                    : "hover:bg-slate-50";
                return (
                <tr key={e.id}
                    className={`border-t border-slate-100 ${rowCls}`}
                    data-testid={`entry-row-${e.id}`}>
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={selected.has(e.id)}
                            onChange={() => toggleSel(e.id)}
                            data-testid={`entry-sel-${e.id}`} />
                  </td>
                  <td className="p-2 text-slate-700">{e.date}</td>
                  <td className="p-2 text-slate-800 max-w-[280px] truncate" title={e.description}>{e.description}</td>
                  <td className={`p-2 text-right font-bold ${
                    e.direction === "gelir" ? "text-emerald-700"
                      : e.direction === "hesap_transferi"
                        ? (e.original_direction === "gelir"
                            ? "text-emerald-700"
                            : e.original_direction === "gider"
                              ? "text-red-700"
                              : "text-blue-600")
                      : "text-red-700"
                  }`}>
                    {(() => {
                      const amt = Number(e.amount_gross).toLocaleString("tr-TR", { minimumFractionDigits: 2 });
                      if (e.direction === "gelir") return `+${amt} ₺`;
                      if (e.direction === "gider") return `-${amt} ₺`;
                      // hesap_transferi — orijinal yöne göre + / -
                      if (e.original_direction === "gelir") return `↔ +${amt} ₺`;
                      if (e.original_direction === "gider") return `↔ -${amt} ₺`;
                      return `↔ ${amt} ₺`;
                    })()}
                  </td>
                  <td className="p-2 text-center">
                    <Badge className={`rounded-none text-[9px] border ${
                      e.direction === "gelir" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                      : e.direction === "hesap_transferi" ? "bg-blue-50 text-blue-700 border-blue-300"
                      : "bg-red-50 text-red-700 border-red-300"
                    }`}>
                      {e.direction === "gelir" ? "GELİR"
                        : e.direction === "hesap_transferi" ? "TRANSFER"
                        : "GİDER"}
                    </Badge>
                  </td>
                  <td className="p-2 text-center">
                    <Badge className={`rounded-none text-[9px] border ${e.nature === "personal" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-blue-50 text-blue-700 border-blue-300"}`}>
                      {e.nature === "personal" ? "KİŞİSEL" : "ŞİRKET"}
                    </Badge>
                  </td>
                  <td className="p-2 text-slate-700 text-[11px]">
                    {e.link_type === "merkez" ? (
                      <Badge className="bg-purple-50 text-purple-800 border border-purple-300 rounded-none text-[9px]">
                        MERKEZ
                      </Badge>
                    ) : e.linked_site_name ? (
                      <span className="text-slate-800">{e.linked_site_name}</span>
                    ) : e.ai_suggested_site_name ? (
                      <span className="text-[10px] text-slate-400 italic">AI: {e.ai_suggested_site_name}</span>
                    ) : "—"}
                  </td>
                  <td className="p-2 text-slate-700 text-[11px]">
                    {e.linked_category || (
                      e.ai_suggested_category ? (
                        <span className="text-[10px] text-slate-400 italic">AI: {e.ai_suggested_category}</span>
                      ) : "—"
                    )}
                  </td>
                  <td className="p-2 text-slate-700 text-[11px]"
                      data-testid={`entry-supplier-${e.id}`}>
                    {e.linked_supplier_name ? (
                      <span className="inline-flex items-center gap-1 text-slate-800">
                        <span className="w-1 h-1 rounded-full bg-[#1B4776]" />
                        {e.linked_supplier_name}
                      </span>
                    ) : e.ai_suggested_supplier_name ? (
                      <span className="text-[10px] text-slate-400 italic">
                        AI: {e.ai_suggested_supplier_name}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {e.status === "pushed" ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-400 rounded-none text-[9px]">
                        <Check size={9} className="mr-0.5" /> AKTARILDI
                      </Badge>
                    ) : e.approved ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-400 rounded-none text-[9px]">
                        <Check size={9} className="mr-0.5" /> ONAYLI
                      </Badge>
                    ) : cat ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-none text-[9px]">
                        KATEGORİZE
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 border border-amber-300 rounded-none text-[9px]">
                        EKSİK
                      </Badge>
                    )}
                  </td>
                  {isStaging && (
                    <td className="p-2 text-right whitespace-nowrap">
                      {(e.ai_suggested_site_id || e.ai_suggested_category || e.ai_suggested_supplier_id) && (
                        <Button variant="outline" size="sm" onClick={() => applyAiSuggestion(e)}
                                data-testid={`entry-ai-${e.id}`}
                                className="rounded-none h-6 border-[#95C11F] text-[#5a7a10] hover:bg-[#F7FBE9] mr-1">
                          <Sparkle size={10} />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => setEditing(e)}
                              data-testid={`entry-edit-${e.id}`}
                              className="rounded-none h-6 border-slate-300">
                        <PencilSimple size={10} />
                      </Button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <EntryEditDialog
          entry={editing}
          sites={sites}
          suppliers={suppliers}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {bulkOpen && (
        <BulkEditDialog
          count={selected.size}
          sites={sites}
          suppliers={suppliers}
          categories={categories}
          onClose={() => setBulkOpen(false)}
          onSaved={async (payload) => {
            try {
              const { data } = await axios.post(`${API}/financial/entries/bulk-update`, {
                entry_ids: Array.from(selected), ...payload,
              });
              const isTransfer = payload.direction === "hesap_transferi";
              const isPersonal = payload.nature === "personal" && !isTransfer;
              if (isTransfer) {
                toast.success(
                  `${data.updated} kayıt Hesap Transferi olarak kategorize edildi — İnceleme & Onay'da kontrol edip onaylayın`
                );
              } else if (isPersonal) {
                toast.success(
                  `${data.updated} kayıt "Kişisel" olarak işaretlendi — proje bağlantısı opsiyonel, bütçeye aktarılmaz`
                );
              } else {
                toast.success(`${data.updated} kayıt güncellendi`);
              }
              setSelected(new Set());   // Bulk-update sonrası seçimi temizle
              setBulkOpen(false);
              await load();
            } catch (e) { toast.error(formatErr(e)); }
          }}
        />
      )}
    </>
  );
}

// Toplu Düzenleme Diyaloğu — sıralı aktivasyon
// (Yön → Harcama Türü → Bağlantı → Kategori → Tedarikçi)
function BulkEditDialog({ count, sites, suppliers, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    setDirection: false, direction: "gider",
    setNature: false, nature: "company",
    setLink: false, link_type: "project", linked_site_id: "",
    setCategory: false, linked_category: "",
    setSupplier: false, linked_supplier_id: "", linked_supplier_name: "",
  });

  // Sıralı aktivasyon / özel mod hesapları
  const isTransferMode = form.setDirection && form.direction === "hesap_transferi";
  const isPersonalMode = form.setNature && form.nature === "personal";

  // Kilit koşulları:
  //  - Nature: Yön seçilmeden aktif olmaz
  //  - Link: Nature seçilmeden aktif olmaz (Transfer'de gizli)
  //  - Category: Link seçilmeden aktif olmaz (Transfer'de gizli)
  //  - Supplier: Category seçilmeden aktif olmaz (Transfer'de gizli)
  // Kişisel modda alanlar OPSİYONEL — kilit yok; kullanıcı isterse proje bağlar,
  // isterse kategori girer. Boş bırakılırsa backend "Kişisel" varsayılan atar.
  const natureLocked = !form.setDirection;
  const linkLocked = !form.setNature || isTransferMode;
  const categoryLocked = !form.setLink || isTransferMode;
  const supplierLocked = !form.setCategory || isTransferMode;

  const submit = () => {
    const payload = {};
    if (form.setDirection) payload.direction = form.direction;
    if (form.setNature) payload.nature = form.nature;
    if (form.setLink) {
      payload.link_type = form.link_type;
      if (form.link_type === "project") payload.linked_site_id = form.linked_site_id;
    }
    if (form.setCategory) {
      payload.linked_category = (form.linked_category || "").trim();
    }
    if (form.setSupplier) {
      if (form.linked_supplier_id) {
        payload.linked_supplier_id = form.linked_supplier_id;
      } else if ((form.linked_supplier_name || "").trim()) {
        payload.linked_supplier_id = "";
        payload.linked_supplier_name = form.linked_supplier_name.trim();
      }
    }
    if (Object.keys(payload).length === 0) { toast.error("En az bir alan seçin"); return; }
    // Transfer modunda bağlantı-kategori zorunluluğu yok (backend otomatik atar)
    if (payload.link_type === "project" && !payload.linked_site_id && !isTransferMode) {
      toast.error("Proje seçin"); return;
    }
    if (form.setCategory && !isTransferMode && !payload.linked_category) {
      toast.error("Kategori boş olamaz"); return;
    }
    onSaved(payload);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-lg" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <PencilSimple size={18} /> Toplu Düzenleme — {count} kayıt
          </DialogTitle>
          <DialogDescription className="text-xs">
            Alanlar sırayla aktifleşir: Yön → Harcama Türü → Bağlantı → Kategori → Tedarikçi.
            Sadece işaretlediğiniz alanlar seçili kayıtlara uygulanır.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* 1. YÖN — her zaman aktif */}
          <BulkField label="1. Yön" testid="bulk-set-direction"
                      checked={form.setDirection}
                      onCheck={(v) => setForm({ ...form, setDirection: v })}>
            <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="bulk-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gelir">Gelir</SelectItem>
                <SelectItem value="gider">Gider</SelectItem>
                <SelectItem value="hesap_transferi">Hesap Transferi (Hesaplar arası)</SelectItem>
              </SelectContent>
            </Select>
            {isTransferMode && (
              <div className="mt-2 p-2 border border-blue-300 bg-blue-50 text-[11px] text-blue-900 flex gap-2"
                    data-testid="bulk-transfer-info">
                <Sparkle size={14} className="text-blue-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold uppercase tracking-widest text-[10px] mb-0.5">
                    Otomatik Kategorizasyon
                  </div>
                  Kategori <strong>"Hesaplar Arası Virman"</strong> olarak otomatik atanır
                  ve bağlantı otomatik <strong>Merkez</strong> olarak işaretlenir. Kayıtlar
                  "İnceleme & Onay"da kalır — kontrol ettikten sonra elle onaylarsınız.
                </div>
              </div>
            )}
          </BulkField>

          {/* 2. HARCAMA TÜRÜ — Yön işaretlenince aktif */}
          <BulkField label="2. Harcama Türü" testid="bulk-set-nature"
                      checked={form.setNature}
                      disabled={natureLocked || isTransferMode}
                      disabledHint={isTransferMode
                        ? "Transfer modunda gerekli değil"
                        : "Önce Yön alanını işaretleyin"}
                      onCheck={(v) => setForm({ ...form, setNature: v })}>
            <Select value={form.nature} onValueChange={(v) => setForm({ ...form, nature: v })}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="bulk-nature"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Şirket adına (Resmi)</SelectItem>
                <SelectItem value="personal">Kişisel</SelectItem>
              </SelectContent>
            </Select>
            {isPersonalMode && (
              <div className="mt-2 p-2 border border-amber-300 bg-amber-50 text-[11px] text-amber-900 flex gap-2"
                    data-testid="bulk-personal-info">
                <Sparkle size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold uppercase tracking-widest text-[10px] mb-0.5">
                    Kişisel Harcama
                  </div>
                  Bağlantı ve kategori <strong>opsiyoneldir</strong> — istenirse proje
                  bilgisi eklenebilir. Bu kayıtlar <strong>bütçeye aktarılmaz</strong>
                  (yalnızca raporlama amaçlı gruplanır).
                </div>
              </div>
            )}
          </BulkField>

          {/* 3. BAĞLANTI — Harcama Türü işaretlenince aktif (Transfer'de kilitli) */}
          <BulkField label="3. Bağlantı" testid="bulk-set-link"
                      checked={form.setLink}
                      disabled={linkLocked}
                      disabledHint={
                        isTransferMode ? "Transfer modunda gerekli değil"
                        : "Önce Harcama Türü alanını işaretleyin"
                      }
                      onCheck={(v) => setForm({ ...form, setLink: v })}>
            <div className="space-y-2">
              <div className="flex gap-2">
                <button type="button"
                        onClick={() => setForm({ ...form, link_type: "project" })}
                        data-testid="bulk-link-project"
                        className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-widest font-bold border-2 transition-colors ${
                          form.link_type === "project"
                            ? "bg-[#1B4776] text-white border-[#1B4776]"
                            : "bg-white text-slate-600 border-slate-300 hover:border-[#1B4776]"
                        }`}>
                  Proje
                </button>
                <button type="button"
                        onClick={() => setForm({ ...form, link_type: "merkez" })}
                        data-testid="bulk-link-merkez"
                        className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-widest font-bold border-2 transition-colors ${
                          form.link_type === "merkez"
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-slate-600 border-slate-300 hover:border-purple-600"
                        }`}>
                  Merkez
                </button>
              </div>
              {form.link_type === "project" && (
                <Select value={form.linked_site_id || "none"} onValueChange={(v) => setForm({ ...form, linked_site_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none h-9 text-sm" data-testid="bulk-site"><SelectValue placeholder="Proje seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Seçin —</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {form.link_type === "merkez" && (
                <div className="text-[11px] text-purple-700 bg-purple-50 border border-purple-200 p-2">
                  <strong>Merkez</strong> — projeye bağlı olmayan merkezi harcama olarak işaretlenecek.
                </div>
              )}
            </div>
          </BulkField>

          {/* 4. KATEGORİ — Bağlantı işaretlenince aktif (Transfer'de kilitli) */}
          <BulkField label="4. Kategori" testid="bulk-set-category"
                      checked={form.setCategory}
                      disabled={categoryLocked}
                      disabledHint={
                        isTransferMode ? "Transfer modunda otomatik atanır"
                        : "Önce Bağlantı alanını işaretleyin"
                      }
                      onCheck={(v) => setForm({ ...form, setCategory: v })}>
            <Input
              list="bulk-cat-list"
              value={form.linked_category}
              onChange={(e) => setForm({ ...form, linked_category: e.target.value })}
              placeholder={isPersonalMode ? "Örn: Kişisel Yemek, Ulaşım... (boş bırakılırsa 'Kişisel' atanır)" : "Kategori yazın veya listeden seçin"}
              className="rounded-none h-9 text-sm"
              data-testid="bulk-category-input"
            />
            <datalist id="bulk-cat-list">
              {categories.map((c) => (<option key={c} value={c} />))}
            </datalist>
            <div className="text-[10px] text-slate-500 mt-1">
              Sistemde olmayan kategori de yazabilirsiniz — serbest metin olarak kaydedilir.
            </div>
          </BulkField>

          {/* 5. TEDARİKÇİ — Kategori işaretlenince aktif */}
          <BulkField label="5. Tedarikçi / Ekip / Personel (opsiyonel)" testid="bulk-set-supplier"
                      checked={form.setSupplier}
                      disabled={supplierLocked}
                      disabledHint={
                        isTransferMode ? "Transfer modunda gerekli değil"
                        : "Önce Kategori alanını işaretleyin"
                      }
                      onCheck={(v) => setForm({ ...form, setSupplier: v })}>
            <Input
              list="bulk-sup-list"
              value={form.linked_supplier_name || ""}
              onChange={(e) => {
                const val = e.target.value;
                const match = suppliers.find((s) => s.name === val);
                setForm({
                  ...form,
                  linked_supplier_name: val,
                  linked_supplier_id: match ? match.id : "",
                });
              }}
              placeholder="Tedarikçi / Ekip / Personel adı yazın veya listeden seçin"
              className="rounded-none h-9 text-sm"
              data-testid="bulk-supplier-input"
            />
            <datalist id="bulk-sup-list">
              {suppliers.map((s) => (<option key={s.id} value={s.name} />))}
            </datalist>
            <div className="text-[10px] text-slate-500 mt-1">
              Listede olmayan tedarikçi de yazabilirsiniz — sistem serbest metin olarak kaydeder.
            </div>
          </BulkField>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none uppercase tracking-widest text-[10px]">
            İptal
          </Button>
          <Button onClick={submit} data-testid="bulk-save"
                  className={`rounded-none uppercase tracking-widest text-[10px] ${
                    isTransferMode
                      ? "bg-blue-600 hover:bg-blue-700"
                      : isPersonalMode
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-[#1B4776] hover:bg-[#153859]"
                  }`}>
            {isTransferMode
              ? <><Check size={12} className="mr-1" />Transfer + Kategorize ({count})</>
              : isPersonalMode
                ? <><Check size={12} className="mr-1" />Kişisel + Kategorize ({count})</>
                : `Uygula (${count} kayıt)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkField({ label, checked, onCheck, children, testid, disabled = false, disabledHint = "" }) {
  return (
    <div
      title={disabled ? disabledHint : ""}
      className={`border p-2.5 transition-colors ${
        disabled
          ? "border-slate-200 bg-slate-50 opacity-60"
          : checked
            ? "border-[#1B4776] bg-[#f7fbfd]"
            : "border-slate-200 bg-white"
      }`}>
      <label className={`flex items-center gap-2 mb-1.5 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
        <input type="checkbox" checked={checked} disabled={disabled}
                onChange={(e) => onCheck(e.target.checked)}
                data-testid={testid} />
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-700">{label}</span>
        {disabled && disabledHint && (
          <span className="text-[9px] normal-case tracking-normal text-slate-400 ml-1">
            — {disabledHint}
          </span>
        )}
      </label>
      <div className={disabled || !checked ? "opacity-40 pointer-events-none" : ""}>
        {children}
      </div>
    </div>
  );
}

function mapCategoryHint(hint, categories) {
  const h = (hint || "").toLowerCase();
  const map = {
    yakit: "Yakıt", akaryakit: "Yakıt",
    market: "Diğer", restoran: "Yemek",
    kredi_karti_odemesi: "Ödeme", taseron_odemesi: "İşçilik",
    kira: "Kira", fatura: "Fatura", malzeme: "Malzeme",
    transfer: "Transfer", ulasim: "Nakliye",
  };
  const mapped = map[h] || hint;
  const found = categories.find((c) => c.toLowerCase() === (mapped || "").toLowerCase());
  return found || mapped || "";
}

function EntryEditDialog({ entry, sites, suppliers, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    direction: entry.direction || "gider",
    nature: entry.nature || "company",
    date: entry.date || "",
    description: entry.description || "",
    amount_gross: entry.amount_gross || 0,
    link_type: entry.link_type || (entry.linked_site_id ? "project" : ""),
    linked_site_id: entry.linked_site_id || "",
    linked_category: entry.linked_category || "",
    linked_supplier_id: entry.linked_supplier_id || "",
    linked_supplier_name: entry.linked_supplier_name || "",
    notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Serbest metin tedarikçi: id yoksa ama isim varsa id'yi boş gönder
      if (!payload.linked_supplier_id && (payload.linked_supplier_name || "").trim()) {
        payload.linked_supplier_id = "";
      }
      await axios.put(`${API}/financial/entries/${entry.id}`, payload);
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">İşlem Detayı</DialogTitle>
          <DialogDescription className="text-xs">
            AI önerileri: {entry.ai_suggested_site_name && `Şantiye: ${entry.ai_suggested_site_name} · `}
            {entry.ai_suggested_category && `Kategori: ${entry.ai_suggested_category} · `}
            {entry.ai_suggested_supplier_name && `Tedarikçi: ${entry.ai_suggested_supplier_name}`}
            {!entry.ai_suggested_site_id && !entry.ai_suggested_category && !entry.ai_suggested_supplier_id && "AI önerisi yok"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Tarih</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="rounded-none h-9 text-sm" data-testid="entry-edit-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Tutar (₺)</Label>
            <Input type="number" step="0.01" value={form.amount_gross}
                    onChange={(e) => setForm({ ...form, amount_gross: parseFloat(e.target.value) || 0 })}
                    className="rounded-none h-9 text-sm" data-testid="entry-edit-amount" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Açıklama</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="rounded-none h-9 text-sm" data-testid="entry-edit-desc" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Yön</Label>
            <Select value={form.direction} onValueChange={(v) => {
              // Hesap transferi seçilirse kategori otomatik doldur, bağlantı temizle
              if (v === "hesap_transferi") {
                setForm({
                  ...form,
                  direction: v,
                  linked_category: form.linked_category || "Hesaplar Arası Virman",
                });
              } else {
                setForm({ ...form, direction: v });
              }
            }}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="entry-edit-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gider">Gider</SelectItem>
                <SelectItem value="gelir">Gelir</SelectItem>
                <SelectItem value="hesap_transferi">Hesap Transferi (Hesaplar arası)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Harcama Türü</Label>
            <Select value={form.nature} onValueChange={(v) => setForm({ ...form, nature: v })}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="entry-edit-nature"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Şirket adına (Resmi)</SelectItem>
                <SelectItem value="personal">Kişisel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Bağlantı</Label>
            {form.direction === "hesap_transferi" && (
              <div className="mb-2 p-2 border border-blue-300 bg-blue-50 text-[11px] text-blue-900 flex gap-2"
                    data-testid="entry-transfer-info">
                <Sparkle size={14} className="text-blue-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold uppercase tracking-widest text-[10px] mb-0.5">
                    Hesap Transferi (Virman)
                  </div>
                  Bu kayıt için bağlantı otomatik <strong>Merkez</strong> olarak,
                  kategori de <strong>"Hesaplar Arası Virman"</strong> olarak atanır.
                  Kayıt "İnceleme & Onay"da kalır — kontrol ettikten sonra elle onaylarsınız.
                </div>
              </div>
            )}
            <div className={`flex gap-2 mb-2 ${form.direction === "hesap_transferi" ? "opacity-50 pointer-events-none" : ""}`}>
              <button type="button"
                      onClick={() => setForm({ ...form, link_type: "project" })}
                      className={`flex-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 ${
                        form.link_type === "project"
                          ? "bg-[#1B4776] text-white border-[#1B4776]"
                          : "bg-white text-slate-600 border-slate-300"
                      }`}>Proje</button>
              <button type="button"
                      onClick={() => setForm({ ...form, link_type: "merkez", linked_site_id: "" })}
                      className={`flex-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 ${
                        form.link_type === "merkez"
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-slate-600 border-slate-300"
                      }`}>Merkez</button>
              <button type="button"
                      onClick={() => setForm({ ...form, link_type: "", linked_site_id: "" })}
                      className={`flex-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 ${
                        !form.link_type
                          ? "bg-slate-200 text-slate-700 border-slate-400"
                          : "bg-white text-slate-600 border-slate-300"
                      }`}>Yok</button>
            </div>
            {form.link_type === "project" && form.direction !== "hesap_transferi" && (
              <Select value={form.linked_site_id || "none"} onValueChange={(v) => setForm({ ...form, linked_site_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-9 text-sm" data-testid="entry-edit-site"><SelectValue placeholder="Proje seçin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Seçin —</SelectItem>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Kategori</Label>
            <Input
              list="entry-cat-list"
              value={form.linked_category}
              onChange={(e) => setForm({ ...form, linked_category: e.target.value })}
              placeholder="Yazın veya listeden seçin"
              className="rounded-none h-9 text-sm"
              data-testid="entry-edit-category"
            />
            <datalist id="entry-cat-list">
              {categories.map((c) => (<option key={c} value={c} />))}
            </datalist>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Tedarikçi / Ekip / Personel (opsiyonel)</Label>
            <Input
              list="entry-sup-list"
              value={form.linked_supplier_name || ""}
              onChange={(e) => {
                const val = e.target.value;
                const match = suppliers.find((s) => s.name === val);
                setForm({
                  ...form,
                  linked_supplier_name: val,
                  linked_supplier_id: match ? match.id : "",
                });
              }}
              placeholder="Yazın veya listeden seçin"
              className="rounded-none h-9 text-sm"
              data-testid="entry-edit-supplier"
            />
            <datalist id="entry-sup-list">
              {suppliers.map((s) => (<option key={s.id} value={s.name} />))}
            </datalist>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Notlar</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="rounded-none text-sm" rows={2} data-testid="entry-edit-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none uppercase tracking-widest text-[10px]">
            İptal
          </Button>
          <Button onClick={save} disabled={saving} data-testid="entry-edit-save"
                  className="rounded-none bg-[#1B4776] hover:bg-[#153859] uppercase tracking-widest text-[10px]">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TAB 5 — Ekstre Özet Sayfası: sınıflandırma + KPI + çapraz kontrol
// ============================================================================
function SummaryTab() {
  const [summaries, setSummaries] = useState([]);      // liste görünümü verisi
  const [selectedStmt, setSelectedStmt] = useState(null); // null = liste, "all" = tüm ekstre özeti, id = tek ekstre detayı
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  // Ekstre özet listesini yükle (kart/tablo görünümü)
  useEffect(() => {
    axios.get(`${API}/financial/statement-summaries`)
      .then((r) => setSummaries(r.data?.items || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    if (!selectedStmt) return;
    setLoading(true);
    try {
      const url = selectedStmt === "all"
        ? `${API}/financial/statement-summary`
        : `${API}/financial/statement-summary?statement_id=${selectedStmt}`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedStmt]);

  const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtSigned = (n) => {
    const v = Number(n || 0);
    const s = v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return v > 0 ? `+${s}` : s;
  };

  const kpi = data?.kpi || {};
  const groups = data?.groups || {};
  const cc = data?.crosscheck || {};

  const groupMeta = [
    { key: "company_expense", label: "Şirket Adına Gider", color: "border-red-400 bg-red-50/40", text: "text-red-700", sign: "-" },
    { key: "company_income", label: "Şirket Adına Gelir", color: "border-emerald-400 bg-emerald-50/40", text: "text-emerald-700", sign: "+" },
    { key: "personal_expense", label: "Kişisel Gider", color: "border-amber-400 bg-amber-50/40", text: "text-amber-700", sign: "-" },
    { key: "personal_income", label: "Kişisel Gelir", color: "border-teal-400 bg-teal-50/40", text: "text-teal-700", sign: "+" },
    { key: "transfer", label: "Hesaplar Arası Transfer (Virman)", color: "border-blue-400 bg-blue-50/40", text: "text-blue-700", sign: "±" },
  ];

  return (
    <div className="space-y-4">
      {/* Liste görünümü — ekstre kartları */}
      {selectedStmt === null && (
        <>
          <Card className="rounded-none border-slate-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
                  Ekstre Listesi
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {summaries.length} ekstre — kayda tıklayın detay görünümüne geçmek için
                </div>
              </div>
              <Button
                onClick={() => setSelectedStmt("all")}
                className="rounded-none bg-[#1B4776] hover:bg-[#153859] uppercase tracking-widest text-[10px] font-bold h-8 px-4"
                data-testid="summary-view-all"
              >
                Tüm Ekstreler (Konsolide)
              </Button>
            </div>
          </Card>
          <Card className="rounded-none border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-700 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left p-2 font-bold">Ekstre</th>
                    <th className="text-left p-2 font-bold">Banka / Dönem</th>
                    <th className="text-right p-2 font-bold text-red-700">Şirket Gider</th>
                    <th className="text-right p-2 font-bold text-emerald-700">Şirket Gelir</th>
                    <th className="text-right p-2 font-bold text-amber-700">Kişisel Gider</th>
                    <th className="text-right p-2 font-bold text-teal-700">Kişisel Gelir</th>
                    <th className="text-right p-2 font-bold text-blue-700 bg-blue-50">Hesap Transferi</th>
                    <th className="text-center p-2 font-bold border-l-2 border-[#1B4776]">Çapraz Kontrol</th>
                    <th className="text-right p-2 font-bold">Kayıt</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.length === 0 && (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-400">
                      Henüz yüklenmiş ekstre yok.
                    </td></tr>
                  )}
                  {summaries.map((s) => (
                    <tr key={s.id}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedStmt(s.id)}
                        data-testid={`summary-list-row-${s.id}`}>
                      <td className="p-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono text-[11px] font-bold text-[#1B4776]">
                            {s.statement_code || "—"}
                          </span>
                          <span className="text-[10px] text-slate-500 max-w-[220px] truncate" title={s.filename}>
                            {s.filename}
                          </span>
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-slate-700 font-medium">{s.bank_name || "—"}</span>
                          <span className="text-[10px] text-slate-500">{s.period_label || "—"}</span>
                        </div>
                      </td>
                      <td className="p-2 text-right text-red-700 font-medium">
                        {s.company_expense ? `-${fmt(s.company_expense)} ₺` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-right text-emerald-700 font-medium">
                        {s.company_income ? `+${fmt(s.company_income)} ₺` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-right text-amber-700 font-medium">
                        {s.personal_expense ? `-${fmt(s.personal_expense)} ₺` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-right text-teal-700 font-medium">
                        {s.personal_income ? `+${fmt(s.personal_income)} ₺` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-right text-blue-700 bg-blue-50/50 font-medium">
                        {s.transfer_net !== 0
                          ? `${s.transfer_net > 0 ? "+" : ""}${fmt(s.transfer_net)} ₺`
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-center border-l-2 border-[#1B4776]">
                        <Badge className={`rounded-none text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${
                          s.matches
                            ? "bg-emerald-50 text-emerald-800 border border-emerald-300"
                            : "bg-amber-50 text-amber-800 border border-amber-300"
                        }`}>
                          {s.matches ? "✓ Tutuyor" : `⚠ Fark: ${fmtSigned(s.difference)}`}
                        </Badge>
                      </td>
                      <td className="p-2 text-right text-slate-600">
                        {s.entry_count}
                        {s.deleted_entry_count > 0 && (
                          <span className="ml-1 text-[9px] text-red-600" title="silinen kayıt sayısı">
                            (−{s.deleted_entry_count})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Detay görünümü — seçili ekstre veya konsolide */}
      {selectedStmt !== null && (
        <>
          <Card className="rounded-none border-slate-200 p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => { setSelectedStmt(null); setData(null); setOpenGroup(null); }}
                variant="outline"
                className="rounded-none border-[#1B4776] text-[#1B4776] hover:bg-[#f7fbfd] uppercase tracking-widest text-[10px] font-bold h-8 px-3"
                data-testid="summary-back-list"
              >
                ← Ekstre Listesi
              </Button>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
                {selectedStmt === "all" ? "Konsolide Özet (Tüm Ekstreler)" : "Ekstre Detayı"}
              </div>
              {data?.statement && (
                <Badge className="bg-[#1B4776] text-white rounded-none font-mono text-[11px] px-2 py-1">
                  {data.statement.statement_code}
                </Badge>
              )}
              <div className="ml-auto text-[10px] text-slate-500">
                {kpi.total_entries || 0} kayıt · {loading && "Yükleniyor..."}
              </div>
            </div>
          </Card>

      {/* KPI kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard label="Toplam Şirket Harcaması" value={kpi.company_expense} fmt={fmt}
                  color="border-red-500 bg-gradient-to-br from-red-50 to-white"
                  textColor="text-red-700" testid="kpi-company-expense" />
        <KpiCard label="Toplam Kişisel Harcama" value={kpi.personal_expense} fmt={fmt}
                  color="border-amber-500 bg-gradient-to-br from-amber-50 to-white"
                  textColor="text-amber-700" testid="kpi-personal-expense" />
        <KpiCard label="Şirketten Net Alacak"
                  value={kpi.net_receivable_from_company} fmt={fmt} signed
                  color="border-[#1B4776] bg-gradient-to-br from-[#f0f6fd] to-white"
                  textColor={kpi.net_receivable_from_company >= 0 ? "text-emerald-700" : "text-red-700"}
                  testid="kpi-net-receivable" />
      </div>

      {/* Kategori grupları */}
      <Card className="rounded-none border-slate-200">
        <div className="p-4 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-widest font-bold text-slate-700">
          Sınıflandırılmış Toplamlar
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 divide-x divide-slate-100">
          {groupMeta.map((g) => {
            const gd = groups[g.key] || {};
            const isTransfer = g.key === "transfer";
            const isOpen = openGroup === g.key;
            return (
              <div key={g.key}
                   className={`p-3 border-b-4 ${g.color} cursor-pointer hover:bg-white transition-colors`}
                   onClick={() => setOpenGroup(isOpen ? null : g.key)}
                   data-testid={`summary-group-${g.key}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{g.label}</div>
                <div className={`mt-2 text-xl font-bold font-heading ${g.text}`}>
                  {isTransfer
                    ? `${fmtSigned(gd.total)} ₺`
                    : `${g.sign} ${fmt(gd.total)} ₺`}
                </div>
                <div className="mt-1 text-[10px] text-slate-500">
                  {gd.count || 0} kayıt · <span className="underline">detayları {isOpen ? "gizle" : "gör"}</span>
                </div>
                {isTransfer && (
                  <div className="mt-2 text-[10px] text-slate-600 space-y-0.5">
                    <div>Gelen: <span className="font-bold text-emerald-700">+{fmt(kpi.transfer_in)} ₺</span></div>
                    <div>Giden: <span className="font-bold text-red-700">-{fmt(kpi.transfer_out)} ₺</span></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Detay tablosu — açılır */}
        {openGroup && (
          <div className="border-t border-slate-200 max-h-[420px] overflow-y-auto"
                data-testid={`summary-detail-${openGroup}`}>
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700 uppercase tracking-wider text-[10px] sticky top-0">
                <tr>
                  <th className="text-left p-2 font-bold">Tarih</th>
                  <th className="text-left p-2 font-bold">Açıklama</th>
                  <th className="text-left p-2 font-bold">Kategori</th>
                  <th className="text-right p-2 font-bold">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {(groups[openGroup]?.entries || []).length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-slate-400">Kayıt yok.</td></tr>
                )}
                {(groups[openGroup]?.entries || []).map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2 text-slate-700">{e.date}</td>
                    <td className="p-2 text-slate-800 max-w-[420px] truncate" title={e.description}>{e.description}</td>
                    <td className="p-2 text-slate-600">
                      {e.linked_category || <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="p-2 text-right font-bold font-mono">
                      {openGroup === "transfer"
                        ? `${fmtSigned(e.signed_amount)} ₺`
                        : `${fmt(e.amount_gross)} ₺`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Çapraz kontrol */}
      <Card className="rounded-none border-slate-200">
        <div className="p-4 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-widest font-bold text-slate-700 flex items-center justify-between gap-3">
          <span>Çapraz Kontrol — Ham vs. Kategorize Toplam</span>
          <Badge className={`rounded-none text-[9px] uppercase tracking-widest ${
            cc.matches
              ? "bg-emerald-50 text-emerald-800 border border-emerald-300"
              : "bg-amber-50 text-amber-800 border border-amber-300"
          }`}>
            {cc.matches ? "✓ TUTUYOR" : "⚠ FARK VAR"}
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-slate-100">
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1.5">
              Ham İşlem Toplamı
              {cc.raw_total_locked && (
                <span
                  title="Ekstre yüklendiği anda sabitlendi — silme/düzenleme sonrası dahi değişmez."
                  className="inline-flex items-center gap-0.5 text-[9px] bg-[#1B4776] text-white px-1.5 py-0.5 tracking-widest font-bold rounded-none"
                  data-testid="crosscheck-locked-badge"
                >
                  🔒 SABİT
                </span>
              )}
            </div>
            <div className="text-[9px] text-slate-500 mb-2">
              {cc.raw_total_locked
                ? "Ekstre parse anında hesaplandı — ana kontrol noktası. Bu değer asla değişmez."
                : "Mevcut kayıtlardan hesaplanan cebirsel toplam"}
            </div>
            <div className={`text-2xl font-bold font-heading ${
              (cc.raw_total || 0) >= 0 ? "text-emerald-700" : "text-red-700"
            }`} data-testid="crosscheck-raw">
              {fmtSigned(cc.raw_total)} ₺
            </div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
              Kategorize Toplamı
            </div>
            <div className="text-[9px] text-slate-500 mb-2">
              Yalnızca <strong>kategori atanmış</strong> işlemlerin cebirsel toplamı
            </div>
            <div className={`text-2xl font-bold font-heading ${
              (cc.categorized_total || 0) >= 0 ? "text-emerald-700" : "text-red-700"
            }`} data-testid="crosscheck-cat">
              {fmtSigned(cc.categorized_total)} ₺
            </div>
          </div>
          <div className={`p-4 ${cc.matches ? "" : "bg-amber-50/40"}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
              Fark
            </div>
            <div className="text-[9px] text-slate-500 mb-2">
              {cc.matches
                ? "Tüm kayıtlar kategorize edilmiş — hesaplar tutuyor."
                : "Kategorize edilmemiş kayıtlar var. İnceleme sekmesinde kalanları kategorize edin."}
            </div>
            <div className={`text-2xl font-bold font-heading ${
              cc.matches ? "text-slate-400" : "text-amber-700"
            }`} data-testid="crosscheck-diff">
              {fmtSigned(cc.difference)} ₺
            </div>
          </div>
        </div>
      </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, fmt, color, textColor, signed = false, testid }) {
  const v = Number(value || 0);
  const display = signed
    ? (v > 0 ? `+${fmt(v)}` : fmt(v))
    : fmt(v);
  return (
    <Card className={`rounded-none border-l-4 ${color} p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">{label}</div>
      <div className={`mt-2 text-3xl font-bold font-heading tracking-tight ${textColor}`}>
        {display} ₺
      </div>
    </Card>
  );
}


// ============================================================================
// TAB 3 — Ortak Borç/Alacak Özeti
// ============================================================================
function DebtTab() {
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState("all");

  const load = async () => {
    try {
      const url = period === "all"
        ? `${API}/financial/debt-summary`
        : `${API}/financial/debt-summary?period=${period}`;
      const { data } = await axios.get(url);
      setRows(data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => {
    axios.get(`${API}/financial/debt-periods`)
      .then((r) => setPeriods(r.data?.items || []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const [pdfBusy, setPdfBusy] = useState(""); // partner_email that is loading

  const exportPartnerPdf = async (partnerEmail) => {
    if (!partnerEmail) return;
    setPdfBusy(partnerEmail);
    try {
      const params = { partner_email: partnerEmail };
      if (period !== "all") params.period = period;
      const resp = await axios.get(`${API}/financial/debt-partner-pdf`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = partnerEmail.replace("@", "-at-").replace(/\./g, "-");
      const periodPart = period === "all" ? "tum" : period;
      a.download = `ortak-ekstre-${safe}-${periodPart}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success(`${partnerEmail} için PDF ekstre indirildi`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPdfBusy(""); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 });

  // "2026-01" → "Ocak 2026" (Türkçe uzun ay)
  const monthLabel = (ym) => {
    if (!ym || ym.length < 7) return ym;
    const [y, m] = ym.split("-");
    const names = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
    return `${names[Number(m) - 1] || m} ${y}`;
  };

  return (
    <Card className="rounded-none border-slate-200">
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
          Ortak Bazında Borç / Alacak Özeti
          {period !== "all" && (
            <Badge className="ml-2 bg-[#1B4776] text-white rounded-none text-[9px] px-2 py-0.5">
              {monthLabel(period)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Dönem:</span>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="rounded-none h-8 text-xs w-[180px]" data-testid="debt-period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Dönemler</SelectItem>
              {periods.map((p) => (
                <SelectItem key={p} value={p}>{monthLabel(p)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[10px] normal-case tracking-normal font-normal text-slate-500 flex items-center gap-1.5">
            <Sparkle size={11} className="text-[#95C11F]" />
            Yalnızca <strong className="text-slate-700">kategorize edilmiş</strong> kayıtlar özete dahildir.
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left p-3 font-bold">Ortak (E-posta)</th>
              <th className="text-right p-3 font-bold">Şirket Adına Gider</th>
              <th className="text-right p-3 font-bold">Şirket Adına Gelir</th>
              <th className="text-right p-3 font-bold">Kişisel Gider</th>
              <th className="text-right p-3 font-bold">Kişisel Gelir</th>
              <th className="text-right p-3 font-bold text-blue-700 bg-blue-50">Hesap Transferi</th>
              <th className="text-right p-3 font-bold border-l-2 border-[#1B4776]">Net Alacak (Şirketten)</th>
              <th className="text-right p-3 font-bold">Kayıt</th>
              <th className="text-center p-3 font-bold w-[70px]">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-slate-400 text-xs">
                Henüz veri yok. Ekstre yükleyip harcama türünü işaretleyin.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.partner_email} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`debt-row-${r.partner_email}`}>
                <td className="p-3 font-medium text-slate-800">{r.partner_email}</td>
                <td className="p-3 text-right text-blue-700 font-medium">{fmt(r.company_expense)} ₺</td>
                <td className="p-3 text-right text-slate-500">{fmt(r.company_income)} ₺</td>
                <td className="p-3 text-right text-amber-700">{fmt(r.personal_expense)} ₺</td>
                <td className="p-3 text-right text-slate-500">{fmt(r.personal_income)} ₺</td>
                <td className="p-3 text-right text-blue-700 bg-blue-50/50 font-medium"
                    title="Hesaplar arası hareket — kümülatif (+/-) toplam"
                    data-testid={`debt-transfer-${r.partner_email}`}>
                  {r.transfer !== 0
                    ? `${r.transfer > 0 ? "+" : ""}${fmt(r.transfer)} ₺`
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className={`p-3 text-right font-bold border-l-2 border-[#1B4776] ${
                  r.net_receivable_from_company > 0
                    ? "text-emerald-700"
                    : r.net_receivable_from_company < 0
                      ? "text-red-700"
                      : "text-slate-500"
                }`}>
                  {r.net_receivable_from_company > 0 ? "+" : ""}{fmt(r.net_receivable_from_company)} ₺
                </td>
                <td className="p-3 text-right text-slate-500 text-xs">{r.total_entries}</td>
                <td className="p-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={pdfBusy === r.partner_email}
                    onClick={() => exportPartnerPdf(r.partner_email)}
                    className="h-7 px-2 rounded-none text-[#1B4776] hover:bg-[#1B4776]/10"
                    title="Bu ortağın kategorize kayıtlarını PDF olarak indir"
                    data-testid={`debt-pdf-${r.partner_email}`}
                  >
                    <FilePdf size={16} weight="duotone" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 space-y-1">
        <div>
          <strong className="text-[#1B4776]">Net Alacak (+):</strong> Şirket ortak/kullanıcıya bu tutar kadar borçlu (ortak şirket adına harcama yapmış).
        </div>
        <div>
          <strong className="text-red-700">Net Alacak (−):</strong> Ortak şirkete bu tutar kadar borçlu.
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// TAB 4 — Whitelist Yönetimi (sadece root)
// ============================================================================
function WhitelistTab() {
  const [data, setData] = useState({ items: [], root_email: "", is_root: false });
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/financial/whitelist`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!adding.trim() || !adding.includes("@")) { toast.error("Geçerli e-posta girin"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/financial/whitelist`, { email: adding.trim() });
      toast.success("Yetkili eklendi");
      setAdding("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const del = async (id, email) => {
    if (!window.confirm(`${email} adresini yetkili listesinden kaldırmak istiyor musunuz?`)) return;
    try {
      await axios.delete(`${API}/financial/whitelist/${id}`);
      toast.success("Kaldırıldı");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <>
      <Card className="rounded-none border-slate-200 p-5 bg-gradient-to-br from-[#F7FBE9] to-white">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#1B4776] mb-2 flex items-center gap-2">
          <ShieldStar size={16} weight="duotone" /> Yeni Yetkili E-posta Ekle
        </div>
        <div className="text-[11px] text-slate-600 mb-3">
          Bu listede olmayan HİÇ KİMSE (yönetici bile) modülü göremez veya URL'e erişemez.
        </div>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="ornek@mssinsaat.com.tr"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="rounded-none h-9 text-sm"
            data-testid="wl-add-input"
          />
          <Button onClick={add} disabled={busy || !adding.trim()}
                  data-testid="wl-add-btn"
                  className="rounded-none bg-[#1B4776] hover:bg-[#153859] uppercase tracking-widest text-[10px] font-bold">
            <Plus size={13} className="mr-1" /> Ekle
          </Button>
        </div>
      </Card>

      <Card className="rounded-none border-slate-200 mt-4">
        <div className="p-4 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-widest font-bold text-slate-700">
          Aktif Yetkililer ({data.items.length + 1})
        </div>
        <div className="divide-y divide-slate-100">
          {/* Root — asla silinemez */}
          <div className="p-3 flex items-center justify-between bg-emerald-50/40" data-testid="wl-root">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-600 flex items-center justify-center rounded-full">
                <ShieldStar size={14} weight="fill" className="text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">{data.root_email}</div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Sistem Sahibi · Kaldırılamaz</div>
              </div>
            </div>
          </div>
          {/* Diğerleri */}
          {data.items.map((it) => (
            <div key={it.id} className="p-3 flex items-center justify-between hover:bg-slate-50" data-testid={`wl-row-${it.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#1B4776] flex items-center justify-center rounded-full">
                  <ShieldStar size={14} weight="duotone" className="text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">{it.email}</div>
                  <div className="text-[10px] text-slate-500">
                    Ekleyen: {it.added_by_name || "—"} · {new Date(it.added_at).toLocaleString("tr-TR")}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => del(it.id, it.email)}
                      data-testid={`wl-del-${it.id}`}
                      className="rounded-none h-7 border-red-300 text-red-700 hover:bg-red-50">
                <Trash size={12} className="mr-1" /> Kaldır
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
